import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {SpendingPlan} from "../../models/spending-plan";
import {SpendingPlanTemplateService} from "../../services/spending-plan-template.service";
import {SpendingPlanService} from "../../services/spending-plan.service";
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {StatementSection} from "../../../tandem-core/models/statements/statement-section";
import {CurrencyService} from "../../../tandem-core/services/currency.service";
import {DialogService} from "../../../tandem-core/services/dialog.service";
import {Timestamp} from "firebase/firestore";
import {Router} from "@angular/router";
import {DateService} from "../../../tandem-core/services/date.service";
import {LocalStorageService} from "../../../tandem-core/services/local-storage.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {HeaderService} from "../../../tandem-core/services/header.service";
import {TandemDatePipe} from "../../../tandem-core/directives/tandem-date.pipe";

@Component({
  selector: 'tandem-spending-plan-form',
  templateUrl: './spending-plan-form.component.html',
  styleUrls: ['./spending-plan-form.component.scss']
})
export class SpendingPlanFormComponent implements OnInit, AfterViewInit {

  @Input() public spendingPlan: SpendingPlan = this.spendingPlanTemplateService.generateDefaultSpendingPlan();
  @Input() public mode: 'create' | 'edit' = 'create';
  @Input() public cacheChanges = false;
  @Input() public user: TandemUser | null = null;


  public currentPageNumber: number = 0;
  public totalPages = 1;
  public dateRangeForm: FormGroup = new FormGroup<any>({});
  public activeSection: StatementSection | undefined;

  expensesMoreThaRevenueBool = false;

  @Output() public submitted: EventEmitter<SpendingPlan> = new EventEmitter<SpendingPlan>();

  @ViewChild('positionDate', {static: false})
  public positionDate?: TemplateRef<any>;

  constructor(private spendingPlanTemplateService: SpendingPlanTemplateService,
              private currencyService: CurrencyService,
              private fb: FormBuilder,
              private dateService: DateService,
              private datePipe: TandemDatePipe,
              private dialogService: DialogService,
              private localStorageService: LocalStorageService,
              private spendingPlanService: SpendingPlanService,
              private router: Router,
              private header: HeaderService) {
  }

  ngOnInit(): void {
    this.totalPages = this.spendingPlan.revenueSections.length + this.spendingPlan.expenseSections.length + 1;
    this.dateRangeForm = this.fb.group({
      startDate: [this.spendingPlan.startDate.toDate().toISOString().substring(0, 10), Validators.required],
      endDate: [this.spendingPlan.endDate.toDate().toISOString().substring(0, 10), Validators.required],
      payYourselfPercentage: [(this.spendingPlan.payYourselfFirstPercentage || 0) * 100, Validators.required],
    },
      {validator: this.dateRangeValidator()})
    this.dateRangeForm.get('payYourselfPercentage')?.valueChanges.subscribe(payYoSelf => {
      this.spendingPlan.payYourselfFirstPercentage = Number(payYoSelf) / 100;
    })
    this.getPageLabel();
  }

  ngAfterViewInit(): void {
    if (this.positionDate) {
      this.header.setTemplateRef(this.positionDate);
    }
  }

  public getPageLabel(): string {

    if (this.currentPageNumber <= this.spendingPlan.revenueSections.length - 1) {
      this.activeSection = this.spendingPlan.revenueSections[this.currentPageNumber]
    } else {
      this.activeSection = this.spendingPlan.expenseSections[this.currentPageNumber - this.spendingPlan.revenueSections.length];
    }

    if (this.currentPageNumber == this.totalPages) {
      this.activeSection = undefined;
    }
    return `${this.currentPageNumber + 1} of ${this.totalPages}`;
  }

  public setPage(pageNum: number): void {
    if (pageNum > this.currentPageNumber && this.dateRangeForm.invalid) {
      window.scrollTo(0, 0);
    } else {
      this.recalculateTotals();
      if( pageNum == -1 || pageNum == 0 || pageNum == 2 || pageNum == 4) {
        if(this.currentPageNumber - pageNum == 1 && this.currentPageNumber == 0) {
          this.dialogService.openConfirmDialog("Do you want to save first?", `It looks like you're about to exit the ${this.spendingPlanService.getTrackingToolName('single', this.user)}, do you want to save your work before leaving the page?`,
            "Save & Exit", "No Thanks", false, false, false).afterClosed().subscribe(pingPong => {
            if(pingPong) {
              this.onSubmit();
            } else {
              this.router.navigate(["/spending-plans"]);
            }
          })
        }
        if(this.currentPageNumber - pageNum == 1 && this.currentPageNumber == 1) {
          this.recalculateTotals();
          this.currentPageNumber = pageNum;
          this.getPageLabel();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
        if(pageNum == 2) {
          let totalPYFExpenses = 0;
          this.spendingPlan.expenseSections[0].categories.forEach(cat => totalPYFExpenses += cat.total);
          if(this.spendingPlan.totalRevenue * (Number(this.dateRangeForm.get('payYourselfPercentage')?.value) / 100) < totalPYFExpenses) {
            this.dialogService.openConfirmDialog("Are you sure you'd like to continue?", "Looks like your plan has you paying yourself more than what you've budgeted. You can modify your Pay Yourself First budget or continue as-is",
              "Continue", "Update Budget", false, false, true).afterClosed().subscribe(holes => {
              if(holes) {
                this.recalculateTotals();
                this.currentPageNumber = pageNum;
                this.getPageLabel();
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }
            })
          } else  {
            this.recalculateTotals();
            this.currentPageNumber = pageNum;
            this.getPageLabel();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }

        }
        if(pageNum == 4) {
          this.expensesMoreThaRevenueBool = this.spendingPlan.totalRevenue - this.spendingPlan.totalExpenses < 0;
          if (this.expensesMoreThaRevenueBool) {
            this.dialogService.openConfirmDialog("Are you sure you'd like to continue?",
              " Looks like your plan has your essential expenses and lifestyle expenses totaling more than what you've budgeted. You can modify your budget or continue as-is",
              "Continue", "Update Budget", false, false, true).afterClosed().subscribe(hddddd => {
              if (hddddd) {
                this.recalculateTotals();
                this.currentPageNumber = pageNum;
                this.getPageLabel();
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }
            })
          }
          else if(!this.expensesMoreThaRevenueBool) {
            this.recalculateTotals();
            this.currentPageNumber = pageNum;
            this.getPageLabel();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        }
      } else {
        this.recalculateTotals();
        this.currentPageNumber = pageNum;
        this.getPageLabel();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  }

  getTrackingToolName(variant: 'single' | 'plural') {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.cashFlowName ? this.user.customTTMetadata.cashFlowName : 'Cash Flow';
    } else {
      return this.user?.customTTMetadata?.cashFlowNamePlural ? `${this.user.customTTMetadata.cashFlowNamePlural}` : 'Cash Flows';
    }
  }

  public onSubmit(): void {
    this.spendingPlan.startDate = this.dateService.getAdjustedTimestampFromDate(new Date(this.dateRangeForm.get('startDate')?.value));
    this.spendingPlan.endDate = this.dateService.getAdjustedTimestampFromDate(new Date(this.dateRangeForm.get('endDate')?.value));
    this.spendingPlan.title = `${this.datePipe.transform(this.spendingPlan.startDate.toDate())} to ${this.datePipe.transform(this.spendingPlan.endDate.toDate())}`;
    if (this.spendingPlan.payYourselfFirstPercentage === undefined) {
      this.spendingPlan.payYourselfFirstPercentage = 0;
    }
    this.submitted.emit(this.spendingPlan);
  }

  public recalculateTotals() {
    let totalRevenue = 0;
    let totalExpenses = 0;
    let netSpendingPlan = 0;

    this.spendingPlan.revenueSections.forEach(revSection => {
      revSection.categories.forEach(category => {
        category.total = 0;
        category.items.forEach(i => {
          totalRevenue += i.value
          category.total += i.value;
        });
      });
    });

    this.spendingPlan.expenseSections.forEach(expenseSection => {
      expenseSection.categories.forEach(category => {
        category.total = 0;
        category.items.forEach(i => {
          totalExpenses += i.value
          category.total += i.value;
        });
      });
    });
    this.spendingPlan.totalRevenue = totalRevenue;
    this.spendingPlan.totalExpenses = totalExpenses;
    this.spendingPlan.netCashFlow = totalRevenue - totalExpenses;

    let totalPYFExpenses = 0;
    this.spendingPlan.expenseSections[0].categories.forEach(cat => totalPYFExpenses += cat.total);
    if (this.cacheChanges) {
      this.localStorageService.saveItem('createSpendingPlan', this.spendingPlan);
    }
  }


  getSummaryText(sectionType: 'revenue' | 'expense', section: StatementSection, i: number): string {
    let total = 0;
    if (sectionType === 'revenue') {
      this.spendingPlan.revenueSections[i].categories.forEach(cat => total += cat.total);
    } else {
      this.spendingPlan.expenseSections[i].categories.forEach(cat => total += cat.total);
    }
    return `Total ${section.name}: ${this.currencyService.formatNumber(total)}`;
  }

  getTotalText() {
    if (this.activeSection) {
      let total = this.activeSection?.categories
        .map(category => category.total)
        .reduce((acc, val) => acc + val, 0) as number;

      return `Total ${this.activeSection?.name}: ${this.currencyService.formatNumber(total)}`;
    }
    return `Net Cash Flow: ${this.currencyService.formatNumber(this.spendingPlan.netCashFlow)}`;
  }

  getPYFBudget(): string {
    let num: number = this.spendingPlan.totalRevenue * (Number(this.dateRangeForm.get('payYourselfPercentage')?.value) / 100);
    return this.currencyService.formatNumber(num);
  }

  getTotalRemaining(): string {
    if (this.currentPageNumber == this.spendingPlan.revenueSections.length || this.currentPageNumber > this.spendingPlan.revenueSections.length) {
      let pyfBudget: number = this.spendingPlan.totalRevenue * (Number(this.dateRangeForm.get('payYourselfPercentage')?.value) / 100);
      return `Total Remaining Budget ${this.currencyService.formatNumber(this.spendingPlan.totalRevenue - this.spendingPlan.totalExpenses)}`;
    }
    return '';
  }
  getTotalRemainingPYFBudget(): string {
    if(this.currentPageNumber == this.spendingPlan.revenueSections.length) {
      let pyfBudget: number = this.spendingPlan.totalRevenue * (Number(this.dateRangeForm.get('payYourselfPercentage')?.value) / 100);
      let totalPYF = 0;
      this.spendingPlan.expenseSections[0].categories.forEach(cat => {
        totalPYF += cat.total;
      })
      return `Total remaining Pay Yourself First Budget: ${this.currencyService.formatNumber(pyfBudget - totalPYF)}`
    }
    return ''
  }
  getTotalRemainingThing() {}
  dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startDate = control.get('startDate')?.value;
      const endDate = control.get('endDate')?.value;
      if(startDate && endDate && startDate > endDate) {
        return {dateRangeError: true};
      }
      return null;
    };
  }
}
